import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Mail, Lock, AlertCircle, Leaf } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const user = await login(email, password);
      navigate(user.is_admin ? "/admin" : "/");
    } catch (err) {
      setError(err.response?.data?.detail || "Invalid email or password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4" style={{ backgroundColor: "#FDFBF7" }} data-testid="login-page">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-[#2F5233] rounded-full flex items-center justify-center mx-auto mb-4">
            <Leaf className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-[#1A1A1A]" style={{ fontFamily: "'Playfair Display', serif" }}>Welcome Back</h1>
          <p className="text-[#5A5A5A] mt-2">Sign in to access your digital library</p>
        </div>

        <div className="bg-white rounded-3xl p-8 border border-[#E6E2D3] shadow-sm">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-semibold text-[#1A1A1A] mb-2">Email Address</label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5A5A5A]" />
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} required
                  placeholder="your@email.com"
                  className="w-full bg-[#F5F5F0] pl-10 pr-4 py-3 rounded-xl border border-transparent focus:outline-none focus:border-[#2F5233] transition-colors"
                  data-testid="login-email" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-semibold text-[#1A1A1A] mb-2">Password</label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5A5A5A]" />
                <input type="password" value={password} onChange={e => setPassword(e.target.value)} required
                  placeholder="Your password"
                  className="w-full bg-[#F5F5F0] pl-10 pr-4 py-3 rounded-xl border border-transparent focus:outline-none focus:border-[#2F5233] transition-colors"
                  data-testid="login-password" />
              </div>
            </div>

            {error && (
              <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl p-3 text-red-600 text-sm" data-testid="login-error">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />{error}
              </div>
            )}

            <button type="submit" disabled={loading}
              className="w-full bg-[#2F5233] text-white py-4 rounded-full font-bold text-lg hover:bg-[#1E3A21] transition-all hover:scale-[1.02] disabled:opacity-60"
              data-testid="login-submit">
              {loading ? "Signing in..." : "Sign In"}
            </button>
          </form>

          <p className="text-center mt-6 text-sm text-[#5A5A5A]">
            Don't have an account?{" "}
            <Link to="/register" className="text-[#2F5233] font-bold hover:underline" data-testid="register-link-from-login">Create one</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
